import React from "react";
import styled from "styled-components";
import { SortableCell } from "@sonder/ui/source/styled/fragments/Table";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { MetricHeadersData } from "core/queries/GetBuildingsMetrics";
import { useAppDispatch, useAppSelector } from "core/store";
import {
  ReportType,
  setBuildingsSortInput,
  setIndividualsSortInput,
} from "reports/slice";
import { SortAscDesc } from "typings/globalGQLTypes";

const CellContent = styled.div`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface StyledSortableCellProps {
  paddingLeft?: string;
}

const StyledSortableCell = styled(SortableCell)<StyledSortableCellProps>`
  background: ${colors.grayF};
  color: ${colors.gray1};
  padding-left: ${props => (props.paddingLeft ? props.paddingLeft : undefined)};
  position: sticky;
  top: 0;
`;

interface ReportsHeaderRowProps {
  metricHeaders: MetricHeadersData[];
}

const getLabelForReportType = (reportType: ReportType) => {
  switch (reportType) {
    case ReportType.Buildings:
      return "Building";
    case ReportType.Individuals:
      return "Individual";
    default:
      return "N/A";
  }
};

const getNewSortOrder = (
  newSortColumn: number,
  sortColumn: number,
  sortOrder: SortAscDesc,
) => {
  if (newSortColumn !== sortColumn) {
    return SortAscDesc.asc;
  }

  return sortOrder === SortAscDesc.asc ? SortAscDesc.desc : SortAscDesc.asc;
};

export const ReportsHeaderRow = ({ metricHeaders }: ReportsHeaderRowProps) => {
  const buildingsSortInput = useAppSelector(
    state => state.reports.buildingsSortInput,
  );
  const individualsSortInput = useAppSelector(
    state => state.reports.individualsSortInput,
  );
  const reportType = useAppSelector(state => state.reports.reportType);
  const dispatch = useAppDispatch();
  const { track } = useSegment();

  const sortInput =
    reportType === ReportType.Buildings
      ? buildingsSortInput
      : individualsSortInput;
  const { column: sortColumn, order: sortOrder } = sortInput;
  const resourceLabel = getLabelForReportType(reportType);
  const resourceSortOrder = sortColumn === -1 ? sortOrder : null;

  const onCellClick = (column: number, label: string) => {
    const newSortInput = {
      column,
      order: getNewSortOrder(column, sortColumn, sortOrder),
    };
    const setFunction =
      reportType === ReportType.Buildings
        ? setBuildingsSortInput
        : setIndividualsSortInput;

    dispatch(setFunction(newSortInput));
    track({
      eventName: "sort_reports_column",
      action: "click",
      element: "reports_header_row",
      props: {
        column: label,
        order: newSortInput.order,
      },
    });
  };

  const renderMetricHeader = (
    metricHeader: MetricHeadersData,
    index: number,
  ) => {
    const { label, tooltip } = metricHeader;
    const metricSortOrder = sortColumn === index ? sortOrder : null;
    return (
      <StyledSortableCell
        key={index}
        paddingLeft="0.571rem"
        sortOrder={metricSortOrder}
        title={tooltip || undefined}
        onClick={() => onCellClick(index, label)}
      >
        <CellContent>{label}</CellContent>
      </StyledSortableCell>
    );
  };

  return (
    <>
      <StyledSortableCell
        sortOrder={resourceSortOrder}
        onClick={() => {
          onCellClick(-1, resourceLabel);
        }}
      >
        {resourceLabel}
      </StyledSortableCell>
      {metricHeaders.map(renderMetricHeader)}
    </>
  );
};
